import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { Requirement } from "../interfaces/folder.types.ts";
import {
  changeStatusRequirementByID,
  downloadRequirementFile,
  fetchRequirementById,
  onLoadAddressOptions,
  onSetAddressFields,
  onSetCountryFields,
  onSetGenderFields,
  onUnsetAddressFields,
  previewFileByURL,
} from "../actions/requirementsActions.tsx";

export interface RequirementsState {
  loading: boolean;
  loadingDownloadFile: boolean;
  activeRequirement: Requirement | null;
  selectOptions: any;
  groupType: "status" | "subfolder";
}

const initialState: RequirementsState = {
  loading: false,
  loadingDownloadFile: false,
  activeRequirement: null,
  selectOptions: {},
  groupType: "status",
};

const requirementsSlice = createSlice({
  name: "requirements",
  initialState,
  reducers: {
    setActiveRequirement: (state, action: PayloadAction<Requirement>) => {
      state.activeRequirement = action.payload;
    },
    clearActiveRequirement: (state) => {
      state.activeRequirement = null;
    },
    setGroupType: (state, action: PayloadAction<"status" | "subfolder">) => {
      state.groupType = action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<RequirementsState>) => {
    builder.addCase(changeStatusRequirementByID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchRequirementById.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(
      fetchRequirementById.fulfilled,
      (state, action: PayloadAction<Requirement>) => {
        state.activeRequirement = action.payload;
        state.loading = false;
      },
    );
    builder.addCase(changeStatusRequirementByID.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(downloadRequirementFile.pending, (state) => {
      state.loadingDownloadFile = true;
    });
    builder.addCase(downloadRequirementFile.fulfilled, (state) => {
      state.loadingDownloadFile = false;
    });
    builder.addCase(downloadRequirementFile.rejected, (state) => {
      state.loadingDownloadFile = false;
    });

    builder.addCase(previewFileByURL.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(previewFileByURL.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(previewFileByURL.rejected, (state) => {
      state.loading = false;
    });

    // Dynamic form options (gender, address, countries, etc)
    builder.addCase(
      onSetGenderFields.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.selectOptions = {
          ...state.selectOptions,
          ...action.payload,
        };
      },
    );
    builder.addCase(
      onSetAddressFields.fulfilled,
      (state, action: PayloadAction<any>) => {
        const { _address, ...formFields } = action.payload;
        state.selectOptions = {
          ...state.selectOptions,
          ...formFields,
        };
      },
    );
    builder.addCase(
      onUnsetAddressFields.fulfilled,
      (state, action: PayloadAction<any>) => {
        const { _address, ...formFields } = action.payload || {};
        state.selectOptions = {
          ...state.selectOptions,
          ...formFields,
        };
      },
    );
    builder.addCase(
      onSetCountryFields.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.selectOptions = {
          ...state.selectOptions,
          ...action.payload,
        };
      },
    );
    builder.addCase(
      onLoadAddressOptions.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.selectOptions = {
          ...state.selectOptions,
          ...action.payload,
        };
      },
    );
  },
});

export const { setActiveRequirement, clearActiveRequirement, setGroupType } =
  requirementsSlice.actions;
export default requirementsSlice.reducer;
